import { useEffect, useState } from 'react';
import type { OppTradeVolumeResponse } from '@/services/oppDetectorApi';
import { useTradeVolumeContext, type TradeVolumeLegState } from './TradeVolumeContext';
import './TradeVolumeStrip.css';

const VOLUME_WINDOWS: { key: keyof OppTradeVolumeResponse; label: string }[] = [
  { key: 'vol_30m_usd', label: '30m' },
  { key: 'vol_1h_usd', label: '1h' },
  { key: 'vol_4h_usd', label: '4h' },
  { key: 'vol_24h_usd', label: '24h' },
];

function formatVolumeUsd(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(0);
}

function formatUpdatedAgoSec(updatedAtMs: number | null | undefined, nowMs: number): string {
  if (updatedAtMs == null || !Number.isFinite(updatedAtMs) || updatedAtMs <= 0) return '—';
  const sec = Math.max(0, Math.floor((nowMs - updatedAtMs) / 1000));
  return `${sec}秒前`;
}

type LegProps = {
  exchangeLabel: string;
  instId: string;
  state: TradeVolumeLegState;
};

function TradeVolumeLeg({ exchangeLabel, instId, state }: LegProps) {
  const { data, loading, error } = state;
  const canFetch = !!instId.trim();

  if (!canFetch) {
    return (
      <span className="realtime-spread-volume-item">
        <b>{exchangeLabel}</b>
        <span className="realtime-spread-volume-meta">—</span>
      </span>
    );
  }

  if (error) {
    return (
      <span className="realtime-spread-volume-item is-error" title={error}>
        <b>{exchangeLabel}</b>
        <span className="realtime-spread-volume-meta">加载失败</span>
      </span>
    );
  }

  if (loading && !data) {
    return (
      <span className="realtime-spread-volume-item">
        <b>{exchangeLabel}</b>
        <span className="realtime-spread-volume-meta">加载中…</span>
      </span>
    );
  }

  if (!data) return null;

  return (
    <span className="realtime-spread-volume-item" title={instId}>
      <b>{exchangeLabel}</b>
      <span className="realtime-spread-volume-meta">
        {VOLUME_WINDOWS.map(({ key, label }, index) => (
          <span key={key}>
            {index > 0 ? ' · ' : null}
            {label} {formatVolumeUsd(data[key] as number)}
          </span>
        ))}
      </span>
    </span>
  );
}

type Props = {
  exchange1: string;
  exchange2: string;
  exchange1InstId: string;
  exchange2InstId: string;
  variant?: 'default' | 'chart' | 'header';
};

export function TradeVolumeStrip({
  exchange1,
  exchange2,
  exchange1InstId,
  exchange2InstId,
  variant = 'default',
}: Props) {
  const { leg1, leg2, updatedAtMs } = useTradeVolumeContext();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div
      className={`realtime-spread-volume-strip${variant === 'chart' ? ' is-chart' : ''}${variant === 'header' ? ' is-header' : ''}`}
      aria-label="合约成交额"
    >
      <span className="realtime-spread-volume-label">成交额</span>
      <TradeVolumeLeg exchangeLabel={exchange1} instId={exchange1InstId} state={leg1} />
      <span className="realtime-spread-volume-sep" aria-hidden="true">
        |
      </span>
      <TradeVolumeLeg exchangeLabel={exchange2} instId={exchange2InstId} state={leg2} />
      <span className="realtime-spread-volume-refresh">trade_ticks · 30s</span>
      <span className="realtime-spread-volume-updated">{formatUpdatedAgoSec(updatedAtMs, nowMs)}</span>
    </div>
  );
}
